import type { CSSProperties, HTMLAttributes, Ref } from "react";

import { avatar } from "~/components/Avatar.css";

export interface AvatarProps extends HTMLAttributes<HTMLSpanElement> {
  /** Initials to display, e.g. "TD" for Teresa Diep. */
  initials: string;
  /**
   * Diameter in pixels. Defaults to 32. Font size scales
   * proportionally so "TD" always fills ~34% of the circle.
   */
  size?: number;
  /**
   * Background color. Any CSS color. Defaults to the primary brand
   * color. Callers can pass a per-user derived hue for visual
   * recognition across a roster. The explicit `| undefined` is for
   * exactOptionalPropertyTypes — it lets callers forward a possibly-
   * undefined value without a conditional spread.
   */
  background?: string | undefined;
  ref?: Ref<HTMLSpanElement>;
}

/**
 * Circular initials badge. Sized and colored via props; typography
 * and layout come from the token layer. Intentionally a <span> so
 * it can sit inline beside text without creating a block break.
 */
export function Avatar({
  initials,
  size = 32,
  background,
  className,
  style,
  ref,
  ...rest
}: AvatarProps) {
  const merged =
    className !== undefined && className.length > 0
      ? `${avatar} ${className}`
      : avatar;
  const inlineStyle: CSSProperties = {
    width: size,
    height: size,
    fontSize: Math.round(size * 0.34),
    ...(background !== undefined ? { backgroundColor: background } : {}),
    ...style,
  };
  return (
    <span ref={ref} className={merged} style={inlineStyle} aria-hidden="true" {...rest}>
      {initials}
    </span>
  );
}
